import { ReactNode, useRef, useState } from "react";
import { FormFieldWrapper } from "./helpers/FieldWrapper";
import { Dropdown } from "@lockerpm/design";
import { IFilterItemV2 } from "../Table/Filter/types";
import { DropdownItemRenderV2 } from "../antdHelpers/DropdownItemRender";
import { DropdownRenderV2 } from "../antdHelpers/DropdownRender";
import { TagV2 } from "../Tag";

interface IFormTagsProps {
  id?: string;
  label: string;
  values: IFilterItemV2[];
  onChangeValues: (newValues: IFilterItemV2[]) => void;
  keyword: string;
  onChangeKeyword: (newKeyword: string) => void;
  options: IFilterItemV2[];
  requiredMark?: boolean;
  placeholder?: string;
  hint?: ReactNode;
  disabled?: boolean;
}

export const FormTags = ({
  id,
  label,
  values,
  onChangeValues,
  keyword,
  onChangeKeyword,
  options,
  requiredMark = false,
  placeholder,
  hint,
  disabled,
}: IFormTagsProps) => {
  const syncWidthId = "tag-wrapper-" + id;

  // ----------------------------- DATA -----------------------------

  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState<boolean>(false);

  // ----------------------------- METHODS -----------------------------

  const onAddValue = (clicked: IFilterItemV2) => {
    onChangeValues([...values, clicked]);
  };

  const onRemoveValue = (clicked: IFilterItemV2) => {
    onChangeValues(values.filter((item) => item.key !== clicked.key));
  };

  // ----------------------------- RENDER -----------------------------

  return (
    <FormFieldWrapper
      label={label}
      requiredMark={requiredMark}
      error={null}
      hint={hint}
    >
      {disabled ? (
        values.length === 0 ? (
          <p className="text-md text-gray-v2-900">---</p>
        ) : (
          values.map((item, index) => (
            <TagV2 key={index} name={item.getLabel()} />
          ))
        )
      ) : (
        <Dropdown
          menu={{
            items: options
              .filter((item) => !values.map((v) => v.key).includes(item.key))
              .map((item) => ({
                key: item.key,
                label: (
                  <DropdownItemRenderV2
                    label={
                      <>
                        {item.leftIcon ? item.leftIcon : null}
                        {item.getLabel()}
                      </>
                    }
                    isSelected={false}
                  />
                ),
              })),
            onClick: ({ key }) => {
              const selected = options.find((item) => item.key === key);
              if (selected) {
                onAddValue(selected);
              }
            },
            onMouseDown: (e) => {
              // Context: both MouseDown and Click will trigger Blur event.
              // We need Blur event to happen AFTER Click event, since we want users to be able to click into dropdown's items.
              // Blurring before clicking will make the dropdown disappear right before click event being triggered.
              // So this preventDefault is to prevent MouseDown triggering Blur.
              e.preventDefault();
            },
          }}
          dropdownRender={(menu) => (
            <DropdownRenderV2 syncWidthId={syncWidthId}>
              {menu}
            </DropdownRenderV2>
          )}
          open={focused}
        >
          <div
            id={syncWidthId}
            style={{
              paddingTop: values.length > 0 ? undefined : "0.5rem",
              paddingBottom: values.length > 0 ? undefined : "0.5rem",
            }}
            className="tags-input"
            onClick={(e) => {
              // If the clicked element is not an input or tag, focus the input
              if (e.target === e.currentTarget) {
                inputRef.current?.focus();
              }
            }}
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={() => {
              setFocused(false);
            }}
          >
            {values.map((item, index) => (
              <TagV2
                key={index}
                name={item.getLabel()}
                onRemove={() => {
                  onRemoveValue(item);
                }}
              />
            ))}

            <input
              type="text"
              ref={inputRef}
              style={{
                paddingLeft: values.length > 0 ? undefined : "0",
              }}
              value={keyword}
              onChange={(e) => {
                onChangeKeyword(e.target.value);
              }}
              placeholder={placeholder}
            />
          </div>
        </Dropdown>
      )}
    </FormFieldWrapper>
  );
};
